// QuoteWizard.jsx
import { useState, useEffect } from 'react';

/* ========== PRICING DATA ========== */
const PROPERTY_TYPES = {
  flat: { label: 'Flat', price: 45 },
  house: { label: 'House', price: 55 },
  maisonette: { label: 'Maisonette', price: 50 },
};

const PROPERTY_SIZES = {
  small: { label: 'Small', multiplier: 1 },
  medium: { label: 'Medium', multiplier: 1.3 },
  large: { label: 'Large', multiplier: 1.6 },
};

const BEDROOM_PRICE = 8;
const BATHROOM_PRICE = 10;
const RECEPTION_PRICE = 8;
const EXTRA_WC_PRICE = 6;

const ADDONS = {
  oven: { label: 'Inside Oven', price: 40 },
  fridge: { label: 'Inside Fridge', price: 20 },
  windows: { label: 'Interior Windows', price: 25 },
  cabinets: { label: 'Inside Cabinets', price: 25 },
  balcony: { label: 'Balcony / Terrace', price: 20 },
  bedLinen: { label: 'Change Bed Linen', price: 10 },
  petHair: { label: 'Pet Hair Removal', price: 20 },
  heavySoiling: { label: 'Heavy Soiling Extra', price: 35 },
  carpetCleaning: { label: 'Carpet Cleaning (per room)', price: 35 },
};

const FREQUENCIES = {
  oneOff: { label: 'One-Off Clean', discount: 0 },
  weekly: { label: 'Weekly (-15%)', discount: 0.15 },
  fortnightly: { label: 'Fortnightly (-10%)', discount: 0.10 },
  monthly: { label: 'Monthly (-5%)', discount: 0.05 },
};

const COMMERCIAL_INDUSTRIES = [
  'Office', 'Medical / Dental', 'Retail',
  'Gym / Fitness', 'Salon / Spa', 'School / Nursery',
  'Restaurant / Café', 'Co-Working Space',
  'Property Management', 'Other',
];

const COMMERCIAL_SERVICES = [
  'Office Cleaning', 'Washrooms', 'Kitchen / Breakroom',
  'Reception Area', 'Floor Care', 'Glass Cleaning',
  'Bins & Waste', 'Sanitising', 'Consumables Supply',
  'Window Cleaning', 'Carpet Cleaning', 'Periodic Deep Cleans',
];

/* ========== STEP INDICATOR ========== */
const StepIndicator = ({ steps, currentStep }) => (
  <div className="qw-steps">
    {steps.map((label, i) => (
      <div
        key={i}
        className={`qw-steps__item ${
          i < currentStep ? 'qw-steps__item--done' : ''
        } ${i === currentStep ? 'qw-steps__item--active' : ''}`}
      >
        <div className="qw-steps__circle">
          {i < currentStep ? '✓' : i + 1}
        </div>
        <span className="qw-steps__label">{label}</span>
      </div>
    ))}
  </div>
);

/* ========== CUSTOMER TYPE SCREEN ========== */
const CustomerTypeScreen = ({ onSelect }) => (
  <div className="qw-screen qw-screen--center">
    <h3 className="qw-screen__heading">What type of cleaning do you need?</h3>
    <p className="qw-screen__sub">Choose an option below to get your personalised quote</p>
    <div className="qw-cta-cards">
      <button className="qw-cta-card" onClick={() => onSelect('residential')}>
        <span className="qw-cta-card__icon">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
        </span>
        <div>
          <span className="qw-cta-card__title">Residential Cleaning</span>
        </div>
      </button>
      <button className="qw-cta-card" onClick={() => onSelect('commercial')}>
        <span className="qw-cta-card__icon">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="2" width="16" height="20" rx="2" ry="2"/><line x1="9" y1="6" x2="9" y2="6.01"/><line x1="15" y1="6" x2="15" y2="6.01"/><line x1="9" y1="10" x2="15" y2="10"/><line x1="9" y1="14" x2="15" y2="14"/><line x1="9" y1="18" x2="12" y2="18"/></svg>
        </span>
        <div>
          <span className="qw-cta-card__title">Commercial Cleaning</span>
        </div>
      </button>
    </div>
  </div>
);

/* ========== HONEYPOT COMPONENT ========== */
const Honeypot = ({ value, onChange }) => (
  <div style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }} aria-hidden="true">
    <label htmlFor="hp-website">Leave this empty</label>
    <input
      type="text"
      id="hp-website"
      name="hp-website"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      tabIndex={-1}
      autoComplete="off"
    />
  </div>
);

/* ========== SUCCESS SCREEN ========== */
const SuccessScreen = ({ type, name, details, onNewBooking, onBackToServices }) => (
  <div className="qw-wizard">
    <div className="qw-screen qw-screen--center" style={{ padding: '2rem 1rem' }}>
      <div className="qw-success-icon">
        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
          <polyline points="22 4 12 14.01 9 11.01"/>
        </svg>
      </div>
      <h3 className="qw-screen__heading">
        {type === 'residential' ? 'Booking Request Sent' : 'Enquiry Sent'}
      </h3>
      <p className="qw-screen__sub">
        {type === 'residential' 
          ? `Thank you${name ? ', ' + name.split(' ')[0] : ''}. We've received your booking${details ? ' for ' + details : ''}.`
          : `Thank you${name ? ', ' + name.split(' ')[0] : ''}. We've received your enquiry${details ? ' for ' + details : ''}.`
        }
      </p>
      <p className="qw-screen__sub" style={{ fontSize: '0.9rem', marginTop: '-0.5rem', maxWidth: '420px', marginLeft: 'auto', marginRight: 'auto' }}>
        We'll be in touch within 24 hours. Need us sooner? Call <a href="tel:+447849040289" style={{ color: 'var(--color-secondary)', fontWeight: 600, whiteSpace: 'nowrap' }}>+44 7849 040289</a>.
      </p>
      <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap', marginTop: '1rem' }}>
        <button className="qw-btn qw-btn--next" onClick={onNewBooking}>
          {type === 'residential' ? 'Book Another Clean' : 'New Enquiry'}
        </button>
        <button className="qw-btn qw-btn--back" onClick={onBackToServices}>
          Back to Services
        </button>
      </div>
    </div>
  </div>
);

/* ========== RESIDENTIAL WIZARD ========== */
const ResidentialWizard = ({ onBack, scrollToTop }) => {
  const [step, setStep] = useState(0);
  const [propertyType, setPropertyType] = useState('flat');
  const [propertySize, setPropertySize] = useState('medium');
  const [bedrooms, setBedrooms] = useState(1);
  const [bathrooms, setBathrooms] = useState(1);
  const [receptions, setReceptions] = useState(1);
  const [extraWC, setExtraWC] = useState(0);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [frequency, setFrequency] = useState('oneOff');
  const [preferredDate, setPreferredDate] = useState('');

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [postcode, setPostcode] = useState('');
  const [notes, setNotes] = useState('');

  const [hpWebsite, setHpWebsite] = useState('');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [submittedName, setSubmittedName] = useState('');
  const [submittedDetails, setSubmittedDetails] = useState('');

  const toggleAddon = (key) => {
    setSelectedAddons((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const resetForm = () => {
    setStep(0);
    setPropertyType('flat');
    setPropertySize('medium');
    setBedrooms(1);
    setBathrooms(1);
    setReceptions(1);
    setExtraWC(0);
    setSelectedAddons([]);
    setFrequency('oneOff');
    setPreferredDate('');
    setName('');
    setEmail('');
    setPhone('');
    setPostcode('');
    setNotes('');
    setHpWebsite('');
    setSubmitStatus(null);
  };

  const calculateQuote = () => {
    const basePropertyPrice = PROPERTY_TYPES[propertyType].price * PROPERTY_SIZES[propertySize].multiplier;
    const extraBedrooms = Math.max(0, bedrooms - 1) * BEDROOM_PRICE;
    const extraBathrooms = Math.max(0, bathrooms - 1) * BATHROOM_PRICE;
    const extraReceptions = Math.max(0, receptions - 1) * RECEPTION_PRICE;
    const wcExtra = extraWC * EXTRA_WC_PRICE;
    const addonsTotal = selectedAddons.reduce((sum, key) => sum + (ADDONS[key]?.price || 0), 0);
    const subtotal = basePropertyPrice + extraBedrooms + extraBathrooms + extraReceptions + wcExtra + addonsTotal;
    const discount = subtotal * FREQUENCIES[frequency].discount;
    const total = Math.round((subtotal - discount) * 100) / 100;
    
    return {
      basePropertyPrice,
      extraBedrooms,
      extraBathrooms,
      extraReceptions,
      wcExtra,
      addonsTotal,
      subtotal,
      discount: Math.round(discount * 100) / 100,
      total,
      propertyLabel: `${PROPERTY_TYPES[propertyType].label}, ${bedrooms} bed, ${PROPERTY_SIZES[propertySize].label.toLowerCase()}`,
    };
  };

  const quote = calculateQuote();
  const residentialSteps = ['Property', 'Extras', 'Schedule', 'Checkout'];

  const handleSubmit = async () => {
    if (hpWebsite) {
      setSubmitStatus('success');
      setSubmittedName(name);
      setSubmittedDetails(`a ${PROPERTY_TYPES[propertyType].label.toLowerCase()} clean at £${quote.total}`);
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch('https://formspree.io/f/meeyvagk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          customerType: 'residential',
          name,
          email,
          phone,
          postcode,
          propertyType: PROPERTY_TYPES[propertyType].label,
          propertySize: PROPERTY_SIZES[propertySize].label,
          bedrooms,
          bathrooms,
          receptions,
          extraWC,
          addons: selectedAddons.map((k) => ADDONS[k].label).join(', ') || 'None',
          frequency: FREQUENCIES[frequency].label,
          preferredDate: preferredDate
            ? new Date(preferredDate).toLocaleDateString('en-GB', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })
            : 'Not specified',
          quoteTotal: `£${quote.total}`,
          notes: notes || 'None',
          _subject: `New Booking: ${name} - ${PROPERTY_TYPES[propertyType].label} - £${quote.total}`,
        }),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setSubmittedName(name);
        setSubmittedDetails(`a ${PROPERTY_TYPES[propertyType].label.toLowerCase()} clean at £${quote.total}`);
      } else {
        throw new Error('Submission failed');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNewBooking = () => {
    resetForm();
    scrollToTop();
  };

  const handleBackToServices = () => {
    resetForm();
    scrollToTop();
    onBack();
  };

  const canAdvance = () => {
    if (step === 3) return name.trim() && email.trim() && phone.trim() && postcode.trim();
    return true;
  };

  if (submitStatus === 'success') {
    return (
      <SuccessScreen
        type="residential"
        name={submittedName}
        details={submittedDetails}
        onNewBooking={handleNewBooking}
        onBackToServices={handleBackToServices}
      />
    );
  }

  return (
    <div className="qw-wizard">
      <StepIndicator steps={residentialSteps} currentStep={step} />
      <div className="qw-body">
        
        <Honeypot value={hpWebsite} onChange={setHpWebsite} />

        <div className="qw-mobile-quote">
          <div className="qw-mobile-quote__bar">
            <span className="qw-mobile-quote__label">Your price</span>
            <span className="qw-mobile-quote__price">£{quote.total}</span>
          </div>
          {quote.discount > 0 && (
            <div className="qw-mobile-quote__breakdown">
              <span className="qw-mobile-quote__discount">
                Includes {FREQUENCIES[frequency].label.split(' ')[0].toLowerCase()} discount
              </span>
            </div>
          )}
        </div>

        {step === 0 && (
          <div className="qw-screen">
            <h3 className="qw-screen__heading">Tell us about your property</h3>
            <p className="qw-screen__sub">This helps us calculate your base price</p>

            <div className="qw-field-group qw-field-group--mt">
              <label className="qw-label">Property Type</label>
              <div className="qw-option-row">
                {Object.entries(PROPERTY_TYPES).map(([key, { label }]) => (
                  <button
                    key={key}
                    className={`qw-option-pill ${propertyType === key ? 'qw-option-pill--active' : ''}`}
                    onClick={() => setPropertyType(key)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="qw-field-group qw-field-group--mt">
              <label className="qw-label">Size</label>
              <div className="qw-option-row">
                {Object.entries(PROPERTY_SIZES).map(([key, { label }]) => (
                  <button
                    key={key}
                    className={`qw-option-pill ${propertySize === key ? 'qw-option-pill--active' : ''}`}
                    onClick={() => setPropertySize(key)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="qw-counters">
              <div className="qw-counter">
                <span className="qw-counter__label">Bedrooms</span>
                <div className="qw-counter__controls">
                  <button className="qw-counter__btn" onClick={() => setBedrooms(Math.max(0, bedrooms - 1))}>−</button>
                  <span className="qw-counter__value">{bedrooms}</span>
                  <button className="qw-counter__btn" onClick={() => setBedrooms(Math.min(6, bedrooms + 1))}>+</button>
                </div>
                {bedrooms > 1 && <span className="qw-counter__extra">+£{(bedrooms - 1) * BEDROOM_PRICE}</span>}
              </div>

              <div className="qw-counter">
                <span className="qw-counter__label">Bathrooms</span>
                <div className="qw-counter__controls">
                  <button className="qw-counter__btn" onClick={() => setBathrooms(Math.max(1, bathrooms - 1))}>−</button>
                  <span className="qw-counter__value">{bathrooms}</span>
                  <button className="qw-counter__btn" onClick={() => setBathrooms(Math.min(4, bathrooms + 1))}>+</button>
                </div>
                {bathrooms > 1 && <span className="qw-counter__extra">+£{(bathrooms - 1) * BATHROOM_PRICE}</span>}
              </div>

              <div className="qw-counter">
                <span className="qw-counter__label">Reception Rooms</span>
                <div className="qw-counter__controls">
                  <button className="qw-counter__btn" onClick={() => setReceptions(Math.max(1, receptions - 1))}>−</button>
                  <span className="qw-counter__value">{receptions}</span>
                  <button className="qw-counter__btn" onClick={() => setReceptions(Math.min(4, receptions + 1))}>+</button>
                </div>
                {receptions > 1 && <span className="qw-counter__extra">+£{(receptions - 1) * RECEPTION_PRICE}</span>}
              </div>

              <div className="qw-counter">
                <span className="qw-counter__label">Extra WC</span>
                <div className="qw-counter__controls">
                  <button className="qw-counter__btn" onClick={() => setExtraWC(Math.max(0, extraWC - 1))}>−</button>
                  <span className="qw-counter__value">{extraWC}</span>
                  <button className="qw-counter__btn" onClick={() => setExtraWC(Math.min(3, extraWC + 1))}>+</button>
                </div>
                {extraWC > 0 && <span className="qw-counter__extra">+£{extraWC * EXTRA_WC_PRICE}</span>}
              </div>
            </div>

            <div className="qw-included">
              <span className="qw-included__title">Always included:</span>
              <span>Kitchen, bathrooms, living areas, bedrooms, dusting, vacuum, mop, bins, mirrors</span>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="qw-screen">
            <h3 className="qw-screen__heading">Add extra services</h3>
            <p className="qw-screen__sub">Only pay for what you need</p>
            <div className="qw-addons-grid">
              {Object.entries(ADDONS).map(([key, { label, price }]) => (
                <button
                  key={key}
                  className={`qw-addon-card ${selectedAddons.includes(key) ? 'qw-addon-card--selected' : ''}`}
                  onClick={() => toggleAddon(key)}
                >
                  <div className="qw-addon-card__check">
                    {selectedAddons.includes(key) ? '✓' : '+'}
                  </div>
                  <div className="qw-addon-card__info">
                    <span className="qw-addon-card__label">{label}</span>
                    <span className="qw-addon-card__price">+£{price}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="qw-screen">
            <h3 className="qw-screen__heading">How often?</h3>
            <p className="qw-screen__sub">Regular cleans save you money</p>
            <div className="qw-option-row qw-option-row--stack">
              {Object.entries(FREQUENCIES).map(([key, { label, discount }]) => (
                <button
                  key={key}
                  className={`qw-option-pill qw-option-pill--block ${frequency === key ? 'qw-option-pill--active' : ''}`}
                  onClick={() => setFrequency(key)}
                >
                  <span>{label}</span>
                  {discount > 0 && (
                    <span className="qw-discount-badge">-{discount * 100}%</span>
                  )}
                </button>
              ))}
            </div>

            <div className="qw-field-group qw-field-group--mt">
              <label className="qw-label">Preferred Date</label>
              <input
                type="date"
                className="qw-input"
                value={preferredDate}
                onChange={(e) => setPreferredDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="qw-screen">
            <h3 className="qw-screen__heading">Almost done</h3>
            <p className="qw-screen__sub">Enter your details and we'll confirm within 24 hours</p>
            <div className="qw-form-fields">
              <div className="qw-field-group">
                <label className="qw-label">Full Name *</label>
                <input type="text" className="qw-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" />
              </div>
              <div className="qw-field-group">
                <label className="qw-label">Email *</label>
                <input type="email" className="qw-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
              </div>
              <div className="qw-field-group">
                <label className="qw-label">Phone *</label>
                <input type="tel" className="qw-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="07849 040289" />
              </div>
              <div className="qw-field-group">
                <label className="qw-label">Postcode *</label>
                <input type="text" className="qw-input" value={postcode} onChange={(e) => setPostcode(e.target.value)} placeholder="E14 9XX" />
              </div>
              <div className="qw-field-group qw-field-group--full">
                <label className="qw-label">Notes (optional)</label>
                <textarea className="qw-input qw-input--textarea" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Parking info, access details, anything we should know..." rows={2} />
              </div>
            </div>
            {submitStatus === 'error' && (
              <div className="qw-status qw-status--error">
                Something went wrong. Please try again or call us on <a href="tel:+447849040289" style={{ color: 'inherit', textDecoration: 'underline' }}>+44 7849 040289</a>.
              </div>
            )}
          </div>
        )}
      </div>

      <div className="qw-footer">
        <div className="qw-footer__nav">
          {step > 0 ? (
            <button className="qw-btn qw-btn--back" onClick={() => setStep(step - 1)}>Back</button>
          ) : (
            <div />
          )}
          {step < 3 ? (
            <button className="qw-btn qw-btn--next" onClick={() => setStep(step + 1)} disabled={!canAdvance()}>
              Continue
            </button>
          ) : (
            <button
              className="qw-btn qw-btn--submit"
              onClick={handleSubmit}
              disabled={!canAdvance() || isSubmitting}
            >
              {isSubmitting ? 'Sending...' : `Book Now — £${quote.total}`}
            </button>
          )}
        </div>

        <div className="qw-sidebar">
          <div className="qw-sidebar__inner">
            <h4 className="qw-sidebar__title">Your Quote</h4>
            <div className="qw-sidebar__line">
              <span>{quote.propertyLabel}</span>
              <span>£{Math.round(quote.basePropertyPrice)}</span>
            </div>
            {quote.extraBedrooms > 0 && (
              <div className="qw-sidebar__line qw-sidebar__line--extra">
                <span>Extra bedrooms</span>
                <span>+£{quote.extraBedrooms}</span>
              </div>
            )}
            {quote.extraBathrooms > 0 && (
              <div className="qw-sidebar__line qw-sidebar__line--extra">
                <span>Extra bathrooms</span>
                <span>+£{quote.extraBathrooms}</span>
              </div>
            )}
            {quote.extraReceptions > 0 && (
              <div className="qw-sidebar__line qw-sidebar__line--extra">
                <span>Extra receptions</span>
                <span>+£{quote.extraReceptions}</span>
              </div>
            )}
            {quote.wcExtra > 0 && (
              <div className="qw-sidebar__line qw-sidebar__line--extra">
                <span>Extra WC</span>
                <span>+£{quote.wcExtra}</span>
              </div>
            )}
            {selectedAddons.map((key) => (
              <div key={key} className="qw-sidebar__line qw-sidebar__line--addon">
                <span>{ADDONS[key].label}</span>
                <span>+£{ADDONS[key].price}</span>
              </div>
            ))}
            <div className="qw-sidebar__divider" />
            <div className="qw-sidebar__line qw-sidebar__line--bold">
              <span>Subtotal</span>
              <span>£{quote.subtotal}</span>
            </div>
            {quote.discount > 0 && (
              <div className="qw-sidebar__line qw-sidebar__line--discount">
                <span>{FREQUENCIES[frequency].label.split(' ')[0]} discount</span>
                <span>−£{quote.discount}</span>
              </div>
            )}
            <div className="qw-sidebar__divider" />
            <div className="qw-sidebar__line qw-sidebar__line--total">
              <span>Total</span>
              <span>£{quote.total}</span>
            </div>
          </div>
        </div>
      </div>

      <button className="qw-back-link" onClick={() => { scrollToTop(); onBack(); }}>
        Choose a different service
      </button>
    </div>
  );
};

/* ========== COMMERCIAL WIZARD ========== */
const CommercialWizard = ({ onBack, scrollToTop }) => {
  const [step, setStep] = useState(0);
  const [businessName, setBusinessName] = useState('');
  const [industry, setIndustry] = useState('Office');
  const [floors, setFloors] = useState(1);
  const [employees, setEmployees] = useState('');
  const [floorArea, setFloorArea] = useState('');
  const [meetingRooms, setMeetingRooms] = useState(0);
  const [toilets, setToilets] = useState(1);
  const [hasKitchen, setHasKitchen] = useState(false);
  const [hasReception, setHasReception] = useState(false);
  const [services, setServices] = useState(['Office Cleaning', 'Washrooms']);
  const [cleaningFrequency, setCleaningFrequency] = useState('daily');
  const [outOfHours, setOutOfHours] = useState(false);
  const [currentCleaner, setCurrentCleaner] = useState('');
  const [contractEnding, setContractEnding] = useState(false);

  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [notes, setNotes] = useState('');

  const [hpWebsite, setHpWebsite] = useState('');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [submittedName, setSubmittedName] = useState('');
  const [submittedDetails, setSubmittedDetails] = useState('');

  const toggleService = (s) => {
    setServices((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]
    );
  };

  const resetForm = () => {
    setStep(0);
    setBusinessName('');
    setIndustry('Office');
    setFloors(1);
    setEmployees('');
    setFloorArea('');
    setMeetingRooms(0);
    setToilets(1);
    setHasKitchen(false);
    setHasReception(false);
    setServices(['Office Cleaning', 'Washrooms']);
    setCleaningFrequency('daily');
    setOutOfHours(false);
    setCurrentCleaner('');
    setContractEnding(false);
    setContactName('');
    setContactEmail('');
    setContactPhone('');
    setNotes('');
    setHpWebsite('');
    setSubmitStatus(null);
  };

  const commercialSteps = ['Premises', 'Services', 'Details', 'Contact'];

  const handleSubmit = async () => {
    if (hpWebsite) {
      setSubmitStatus('success');
      setSubmittedName(contactName);
      setSubmittedDetails(businessName || 'your business');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch('https://formspree.io/f/meeyvagk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          customerType: 'commercial',
          businessName,
          contactName,
          contactEmail,
          contactPhone,
          industry,
          floors,
          employees,
          floorArea,
          meetingRooms,
          toilets,
          hasKitchen: hasKitchen ? 'Yes' : 'No',
          hasReception: hasReception ? 'Yes' : 'No',
          services: services.join(', '),
          cleaningFrequency,
          outOfHours: outOfHours ? 'Yes' : 'No',
          currentCleaner: currentCleaner || 'None',
          contractEnding: contractEnding ? 'Yes' : 'No',
          notes: notes || 'None',
          _subject: `Commercial Enquiry: ${businessName || 'New Business'} - ${industry}`,
        }),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setSubmittedName(contactName);
        setSubmittedDetails(businessName || 'your business');
      } else {
        throw new Error('Submission failed');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNewBooking = () => {
    resetForm();
    scrollToTop();
  };

  const handleBackToServices = () => {
    resetForm();
    scrollToTop();
    onBack();
  };

  const canAdvance = () => {
    if (step === 0) return businessName.trim() && employees;
    if (step === 3) return contactName.trim() && contactEmail.trim() && contactPhone.trim();
    return true;
  };

  if (submitStatus === 'success') {
    return (
      <SuccessScreen
        type="commercial"
        name={submittedName}
        details={submittedDetails}
        onNewBooking={handleNewBooking}
        onBackToServices={handleBackToServices}
      />
    );
  }

  return (
    <div className="qw-wizard">
      <StepIndicator steps={commercialSteps} currentStep={step} />
      <div className="qw-body">
        
        <Honeypot value={hpWebsite} onChange={setHpWebsite} />

        {step === 0 && (
          <div className="qw-screen">
            <h3 className="qw-screen__heading">Tell us about your premises</h3>
            <div className="qw-form-fields">
              <div className="qw-field-group qw-field-group--full">
                <label className="qw-label">Business Name *</label>
                <input type="text" className="qw-input" value={businessName} onChange={(e) => setBusinessName(e.target.value)} placeholder="e.g., Acme Corp Ltd" />
              </div>
              <div className="qw-field-group">
                <label className="qw-label">Industry</label>
                <select className="qw-input qw-input--select" value={industry} onChange={(e) => setIndustry(e.target.value)}>
                  {COMMERCIAL_INDUSTRIES.map((ind) => (
                    <option key={ind} value={ind}>{ind}</option>
                  ))}
                </select>
              </div>
              <div className="qw-field-group">
                <label className="qw-label">Floors</label>
                <input type="number" className="qw-input" value={floors} onChange={(e) => setFloors(parseInt(e.target.value) || 1)} min={1} />
              </div>
              <div className="qw-field-group">
                <label className="qw-label">Employees *</label>
                <input type="text" className="qw-input" value={employees} onChange={(e) => setEmployees(e.target.value)} placeholder="e.g., 10-50" />
              </div>
              <div className="qw-field-group">
                <label className="qw-label">Floor Area</label>
                <input type="text" className="qw-input" value={floorArea} onChange={(e) => setFloorArea(e.target.value)} placeholder="e.g., 2,000 sq ft" />
              </div>
              <div className="qw-field-group">
                <label className="qw-label">Meeting Rooms</label>
                <input type="number" className="qw-input" value={meetingRooms} onChange={(e) => setMeetingRooms(parseInt(e.target.value) || 0)} min={0} />
              </div>
              <div className="qw-field-group">
                <label className="qw-label">Toilets</label>
                <input type="number" className="qw-input" value={toilets} onChange={(e) => setToilets(parseInt(e.target.value) || 1)} min={1} />
              </div>
              <div className="qw-field-group qw-field-group--checkbox-group">
                <label className="qw-checkbox">
                  <input type="checkbox" checked={hasKitchen} onChange={(e) => setHasKitchen(e.target.checked)} />
                  <span>Kitchen / Breakroom</span>
                </label>
                <label className="qw-checkbox">
                  <input type="checkbox" checked={hasReception} onChange={(e) => setHasReception(e.target.checked)} />
                  <span>Reception Area</span>
                </label>
              </div>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="qw-screen">
            <h3 className="qw-screen__heading">What services do you need?</h3>
            <div className="qw-addons-grid qw-addons-grid--cols3">
              {COMMERCIAL_SERVICES.map((s) => (
                <button
                  key={s}
                  className={`qw-addon-card qw-addon-card--small ${services.includes(s) ? 'qw-addon-card--selected' : ''}`}
                  onClick={() => toggleService(s)}
                >
                  <div className="qw-addon-card__check">{services.includes(s) ? '✓' : '+'}</div>
                  <span className="qw-addon-card__label">{s}</span>
                </button>
              ))}
            </div>
            <div className="qw-field-group qw-field-group--mt">
              <label className="qw-label">Frequency</label>
              <div className="qw-option-row">
                {['daily', '3x weekly', 'weekly', 'fortnightly', 'monthly'].map((f) => (
                  <button
                    key={f}
                    className={`qw-option-pill ${cleaningFrequency === f ? 'qw-option-pill--active' : ''}`}
                    onClick={() => setCleaningFrequency(f)}
                  >
                    {f.charAt(0).toUpperCase() + f.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            <label className="qw-checkbox qw-checkbox--mt">
              <input type="checkbox" checked={outOfHours} onChange={(e) => setOutOfHours(e.target.checked)} />
              <span>Out-of-hours cleaning needed</span>
            </label>
          </div>
        )}

        {step === 2 && (
          <div className="qw-screen">
            <h3 className="qw-screen__heading">Additional information</h3>
            <div className="qw-form-fields">
              <div className="qw-field-group qw-field-group--full">
                <label className="qw-label">Current cleaner?</label>
                <input type="text" className="qw-input" value={currentCleaner} onChange={(e) => setCurrentCleaner(e.target.value)} placeholder="If yes, which company?" />
              </div>
              <div className="qw-field-group qw-field-group--full">
                <label className="qw-checkbox">
                  <input type="checkbox" checked={contractEnding} onChange={(e) => setContractEnding(e.target.checked)} />
                  <span>Current contract ending soon</span>
                </label>
              </div>
              <div className="qw-field-group qw-field-group--full">
                <label className="qw-label">Notes (optional)</label>
                <textarea className="qw-input qw-input--textarea" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Any specific requirements, access details, or questions..." rows={3} />
              </div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="qw-screen">
            <h3 className="qw-screen__heading">Your contact details</h3>
            <p className="qw-screen__sub">We'll send your tailored quote within 24 hours</p>
            <div className="qw-form-fields">
              <div className="qw-field-group">
                <label className="qw-label">Contact Name *</label>
                <input type="text" className="qw-input" value={contactName} onChange={(e) => setContactName(e.target.value)} placeholder="Your name" />
              </div>
              <div className="qw-field-group">
                <label className="qw-label">Email *</label>
                <input type="email" className="qw-input" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} placeholder="you@company.com" />
              </div>
              <div className="qw-field-group">
                <label className="qw-label">Phone *</label>
                <input type="tel" className="qw-input" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} placeholder="020 7123 4567" />
              </div>
              <div className="qw-field-group qw-field-group--full">
                <label className="qw-label">Notes (optional)</label>
                <textarea className="qw-input qw-input--textarea" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Any specific requirements..." rows={2} />
              </div>
            </div>
            {submitStatus === 'error' && (
              <div className="qw-status qw-status--error">
                Something went wrong. Please try again or call us on <a href="tel:+447849040289" style={{ color: 'inherit', textDecoration: 'underline' }}>+44 7849 040289</a>.
              </div>
            )}
          </div>
        )}
      </div>

      <div className="qw-footer qw-footer--commercial">
        <div className="qw-footer__nav">
          {step > 0 ? (
            <button className="qw-btn qw-btn--back" onClick={() => setStep(step - 1)}>Back</button>
          ) : (
            <div />
          )}
          {step < 3 ? (
            <button className="qw-btn qw-btn--next" onClick={() => setStep(step + 1)} disabled={!canAdvance()}>Continue</button>
          ) : (
            <button className="qw-btn qw-btn--submit" onClick={handleSubmit} disabled={!canAdvance() || isSubmitting}>
              {isSubmitting ? 'Sending...' : 'Get My Free Quote'}
            </button>
          )}
        </div>
        <div className="qw-sidebar qw-sidebar--commercial">
          <div className="qw-sidebar__inner">
            <h4 className="qw-sidebar__title">What happens next?</h4>
            <div className="qw-sidebar__info">
              <div className="qw-sidebar__info-step">
                <span className="qw-sidebar__info-num">1</span>
                <span>We review your requirements</span>
              </div>
              <div className="qw-sidebar__info-step">
                <span className="qw-sidebar__info-num">2</span>
                <span>Free site survey if needed</span>
              </div>
              <div className="qw-sidebar__info-step">
                <span className="qw-sidebar__info-num">3</span>
                <span>Tailored proposal within 24h</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <button className="qw-back-link" onClick={() => { scrollToTop(); onBack(); }}>
        Choose a different service
      </button>
    </div>
  );
};

/* ========== MAIN EXPORT ========== */
const QuoteWizard = ({ preselectedService, onServiceChange }) => {
  const [customerType, setCustomerType] = useState(null);

  useEffect(() => {
    if (preselectedService === 'residential' || preselectedService === 'commercial') {
      setCustomerType(preselectedService);
    }
  }, [preselectedService]);

  const handleCustomerTypeSelect = (type) => {
    setCustomerType(type);
    if (onServiceChange) onServiceChange(type);
  };

  const handleBack = () => {
    setCustomerType(null);
    if (onServiceChange) onServiceChange(null);
  };

  const scrollToWizardTop = () => {
    const quoteSection = document.getElementById('quote');
    if (quoteSection) {
      const yOffset = -80;
      const y = quoteSection.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  return (
    <section className="section section--light" id="quote">
      <div className="container">
        <div className="section__header">
          <span className="section__tag">Get Started</span>
          <h2 className="section__title">Build Your Quote</h2>
          <p className="section__subtitle">
            {customerType === 'residential'
              ? 'Build your clean and see the price update instantly.'
              : customerType === 'commercial'
              ? 'Tell us about your business for a tailored proposal.'
              : 'Select what you need cleaning and we will calculate your price.'}
          </p>
        </div>

        <div className="qw-container">
          {!customerType && <CustomerTypeScreen onSelect={handleCustomerTypeSelect} />}
          {customerType === 'residential' && <ResidentialWizard onBack={handleBack} scrollToTop={scrollToWizardTop} />}
          {customerType === 'commercial' && <CommercialWizard onBack={handleBack} scrollToTop={scrollToWizardTop} />}
        </div>
      </div>
    </section>
  );
};

export default QuoteWizard;